import { MouseEvent, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { WPPost, WPMedia } from '../../types/wordpress';
import Card from '../ui/Card';
import QuietAction from '../cards/QuietAction';
import SaveToMyOrbconButton from '../saved/SaveToMyOrbconButton';
import { buildResourcePath } from '../../utils/resourceLinks';
import { resourceClinical, resourceOffer } from '../../utils/cardFacts';
import { isUnderRevision } from '../../utils/resourceRevision';
import RevisionNotice from './RevisionNotice';
import ResourceKindLabel from './ResourceKindLabel';
import { decodeHtmlEntities } from '../../utils/htmlEntities';
import {
  beginPlaceTransition,
  isPlainClick,
  openPlace,
  PlaceRoom,
  rememberPlaceRoom,
  rememberResourcePost,
  usePlaceOrigin,
} from '../../utils/placeMotion';
import { programSlugFromPath, rememberProgramScan } from '../../utils/programScan';

interface ResourceCardProps {
  post: WPPost;
  view?: 'grid' | 'list';
  fromHubSlug?: string;
  attachments?: WPMedia[];
  room?: PlaceRoom;
  originVia?: string;
  /** Opened from a hub section on the program page, so Back returns there. */
  returnToProgram?: boolean;
  /** Kept so existing scan callers still typecheck. The card shows PDF or a file count. */
  summarizeAttachments?: boolean;
  onOpen?: (event: MouseEvent<HTMLAnchorElement>) => void;
}

const PLACEHOLDER_BYLINE = 'First author, Second author';

export default function ResourceCard({
  post,
  fromHubSlug,
  attachments = [],
  room,
  originVia,
  returnToProgram = false,
  onOpen,
}: ResourceCardProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const cardRef = useRef<HTMLDivElement>(null);
  usePlaceOrigin(cardRef, 'resource', post.slug, originVia);
  const resourcePath = buildResourcePath(post.slug, fromHubSlug);
  const title = decodeHtmlEntities(post.title.rendered);
  const clinical = resourceClinical(post.excerpt?.rendered ?? '');
  const underRevision = isUnderRevision(post.acf?.under_revision);
  const offer = resourceOffer(attachments);
  const published = new Date(post.date);
  const date = Number.isNaN(published.getTime()) ? '' : format(published, 'MMMM d, yyyy');

  const openResource = (event: MouseEvent<HTMLAnchorElement>) => {
    if (onOpen) {
      onOpen(event);
      return;
    }
    rememberResourcePost(post);
    if (!fromHubSlug || !room) {
      rememberPlaceRoom(null);
      openPlace(
        event,
        cardRef.current,
        { kind: 'resource', slug: post.slug, via: originVia },
        resourcePath,
        navigate,
        location
      );
      return;
    }
    rememberPlaceRoom({
      ...room,
      crumbs: [...room.crumbs, { label: title }],
    });
    if (!isPlainClick(event)) return;
    event.preventDefault();
    const current =
      location.state && typeof location.state === 'object'
        ? { ...(location.state as Record<string, unknown>) }
        : {};
    delete current.restoreProgramScan;
    delete current.documentBack;
    delete current.focusHubSlug;
    delete current.hubReturn;
    if (returnToProgram) {
      const programSlug = programSlugFromPath(room.to);
      if (programSlug) rememberProgramScan(programSlug);
      current.documentBack = 'program';
      if (fromHubSlug) current.focusHubSlug = fromHubSlug;
    } else {
      current.hubReturn = `${location.pathname}${location.search}${location.hash}`;
    }
    beginPlaceTransition('deeper', () => navigate(resourcePath, { state: current }));
  };

  return (
    <Card ref={cardRef} className="!p-4">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <ResourceKindLabel postId={post.id} title={title} />
          <h3 className="mt-1 text-lg font-semibold text-gray-900">{title}</h3>
        </div>
        <SaveToMyOrbconButton
          type="RESOURCE"
          postId={post.id}
          title={title}
          slug={post.slug}
          variant="icon"
          align="end"
        />
      </div>
      <p className="mt-2 text-sm text-gray-600">{PLACEHOLDER_BYLINE}</p>
      {clinical && <p className="mt-2 text-sm text-gray-600">{clinical}</p>}
      <div className="mt-3">
        {underRevision && <RevisionNotice className="mb-1" />}
        <p className="text-sm text-gray-600">{offer ? `${date} · ${offer}` : date}</p>
      </div>
      <div className="mt-3">
        <span className="inline-flex min-h-[44px] items-center">
          <QuietAction to={resourcePath} label={`Open ${title}`} onClick={openResource}>
            Open
          </QuietAction>
        </span>
      </div>
    </Card>
  );
}
